import React, { useEffect, useRef, useState } from 'react';
import { useQuill } from 'react-quilljs';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

// Register custom inline style-based font size attributor in Quill
const Size = Quill.import('attributors/style/size');
const fontSizeArr = ['12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px'];
Size.whitelist = fontSizeArr;
Quill.register(Size, true);

// ── Simple HTML prettifier (no extra dependencies needed) ──────────────────
const formatHtml = (html) => {
  if (!html || !html.trim()) return '';
  const INDENT = '  ';
  const VOID_TAGS = new Set(['area','base','br','col','embed','hr','img','input','link','meta','param','source','track','wbr']);
  // Tokenise: split on tags
  const tokens = html.split(/(<[^>]+>)/g).filter(t => t !== '');
  let indent = 0;
  let result = '';

  tokens.forEach(token => {
    const isTag = token.startsWith('<');
    if (!isTag) {
      const text = token.trim();
      if (text) result += INDENT.repeat(indent) + text + '\n';
      return;
    }
    const isClose    = /^<\//.test(token);
    const isSelfClose= /\/>$/.test(token) || VOID_TAGS.has((token.match(/^<([a-z]+)/i) || [])[1]?.toLowerCase() || '');
    const isOpen     = !isClose && !isSelfClose;

    if (isClose) {
      indent = Math.max(0, indent - 1);
      result += INDENT.repeat(indent) + token + '\n';
    } else if (isSelfClose) {
      result += INDENT.repeat(indent) + token + '\n';
    } else {
      result += INDENT.repeat(indent) + token + '\n';
      if (isOpen) indent++;
    }
  });

  return result.trim();
};

// ── Component ──────────────────────────────────────────────────────────────
const CustomEditor = ({ data, onChange, placeholder = 'Start typing...' }) => {
  const { quill, quillRef } = useQuill({
    placeholder: placeholder,
    modules: {
      toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ size: fontSizeArr }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link', 'image', 'video'],
        ['clean'],
      ],
    },
  });

  const [isHtmlMode, setIsHtmlMode]   = useState(false);
  const [htmlValue, setHtmlValue]     = useState(data || '');
  const isUpdating                    = useRef(false);
  const initialised                   = useRef(false);

  // ── One-time initialisation: load initial data into Quill ─────────────
  useEffect(() => {
    if (quill && !initialised.current && data !== undefined) {
      initialised.current = true;
      quill.clipboard.dangerouslyPasteHTML(data || '');
    }
  }, [quill, data]);

  // ── Quill text-change → bubble HTML up to parent ───────────────────────
  useEffect(() => {
    if (!quill) return;
    const handleTextChange = () => {
      if (isHtmlMode) return;
      isUpdating.current = true;
      const raw       = quill.root.innerHTML;
      const cleanHtml = raw === '<p><br></p>' ? '' : raw;
      setHtmlValue(cleanHtml);
      onChange(cleanHtml);
      setTimeout(() => { isUpdating.current = false; }, 20);
    };
    quill.on('text-change', handleTextChange);
    return () => quill.off('text-change', handleTextChange);
  }, [quill, onChange, isHtmlMode]);

  // ── Toggle handler ─────────────────────────────────────────────────────
  const handleToggle = (toHtml) => {
    if (toHtml) {
      // Visual → HTML: read DIRECTLY from Quill so new-blog content is captured
      let currentHtml = '';
      if (quill) {
        const raw = quill.root.innerHTML;
        currentHtml = raw === '<p><br></p>' ? '' : raw;
      }
      const formatted = formatHtml(currentHtml);
      setHtmlValue(formatted);
      onChange(currentHtml);   // keep parent state in sync
      setIsHtmlMode(true);
    } else {
      // HTML → Visual: push textarea content back into Quill
      setIsHtmlMode(false);
      setTimeout(() => {
        if (quill) {
          isUpdating.current = true;
          quill.clipboard.dangerouslyPasteHTML(htmlValue || '');
          onChange(htmlValue || '');
          setTimeout(() => { isUpdating.current = false; }, 20);
        }
      }, 50);
    }
  };

  // ── HTML textarea change ───────────────────────────────────────────────
  const handleHtmlChange = (e) => {
    const val = e.target.value;
    setHtmlValue(val);
    onChange(val);
  };

  // ── Format button ──────────────────────────────────────────────────────
  const handleFormat = () => {
    const formatted = formatHtml(htmlValue);
    setHtmlValue(formatted);
    onChange(formatted);
  };

  return (
    <div className="custom-editor-container border rounded" style={{ borderColor: '#ced4da', background: '#fff' }}>
      {/* ── Scoped styles ─────────────────────────────────────────────── */}
      <style>{`
        .ql-snow .ql-picker.ql-size { width: 100px; }
        .ql-snow .ql-picker.ql-size .ql-picker-label::before,
        .ql-snow .ql-picker.ql-size .ql-picker-item::before  { content: '16px' !important; }
        .ql-snow .ql-picker.ql-size .ql-picker-label[data-value]::before,
        .ql-snow .ql-picker.ql-size .ql-picker-item[data-value]::before { content: attr(data-value) !important; }

        .custom-editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8f9fa;
          border-bottom: 1px solid #ced4da;
          padding: 7px 12px;
          border-top-left-radius: 4px;
          border-top-right-radius: 4px;
          flex-wrap: wrap;
          gap: 6px;
        }
        .custom-editor-title { font-size: 13px; font-weight: 600; color: #495057; }
        .custom-editor-actions { display: flex; align-items: center; gap: 8px; }
        .editor-mode-btn { font-size: 12px; padding: 4px 10px; font-weight: 500; border: 1px solid transparent; }
        .ql-container.ql-snow { border: none !important; }
        .ql-toolbar.ql-snow { border: none !important; border-bottom: 1px solid #e9ecef !important; background: #fdfdfd; }
        .custom-editor-html-wrap { position: relative; }
        .custom-editor-html-textarea {
          width: 100%; min-height: 350px; border: none; outline: none;
          padding: 16px; padding-top: 14px;
          font-family: 'Courier New', Courier, monospace; font-size: 13px;
          line-height: 1.6; color: #1e2330; background: #f9fafc;
          resize: vertical; display: block; tab-size: 2;
        }
      `}</style>

      {/* ── Header / mode toggle ──────────────────────────────────────── */}
      <div className="custom-editor-header">
        <div className="custom-editor-title">
          {isHtmlMode
            ? <span className="text-secondary"><i className="ri-code-s-slash-line me-1"></i>HTML Source View</span>
            : <span className="text-primary"><i className="ri-edit-2-line me-1"></i>Visual Editor View</span>}
        </div>

        <div className="custom-editor-actions">
          {/* Auto-format button — only visible in HTML mode */}
          {isHtmlMode && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleFormat}
              title="Auto-format / Prettify HTML"
              style={{ fontSize: 11, padding: '3px 10px' }}
            >
              <i className="ri-magic-line me-1"></i>Auto Format
            </button>
          )}

          {/* View toggle */}
          <div className="btn-group rounded border" style={{ overflow: 'hidden' }}>
            <button
              type="button"
              className={`btn btn-sm editor-mode-btn ${!isHtmlMode ? 'btn-primary' : 'btn-light'}`}
              onClick={() => handleToggle(false)}
              style={{ borderRadius: 0 }}
            >
              Visual
            </button>
            <button
              type="button"
              className={`btn btn-sm editor-mode-btn ${isHtmlMode ? 'btn-primary' : 'btn-light'}`}
              onClick={() => handleToggle(true)}
              style={{ borderRadius: 0 }}
            >
              HTML / Source
            </button>
          </div>
        </div>
      </div>

      {/* ── Editor content area ───────────────────────────────────────── */}
      <div className="editor-content-wrapper">
        {/* Quill — always mounted so internal state is preserved, hidden when in HTML mode */}
        <div style={{ display: isHtmlMode ? 'none' : 'block' }}>
          <div className="snow-editor" style={{ minHeight: '300px' }}>
            <div ref={quillRef} />
          </div>
        </div>

        {/* HTML textarea */}
        {isHtmlMode && (
          <div className="custom-editor-html-wrap">
            <textarea
              className="custom-editor-html-textarea"
              value={htmlValue}
              onChange={handleHtmlChange}
              spellCheck={false}
              placeholder="Paste or type raw HTML / plain text content here…"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomEditor;
